import i18n from '../../i18n';
import type { ConversationContact, ConversationSummary } from '../../lib/api';
import { formatDate, formatRelative, formatTime, formatWhen } from '../../lib/format';

export function contactName(contact: ConversationContact): string {
  const full = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
  return full || contact.username || contact.email || contact.phone || i18n.t('inbox.unknownContact');
}

/** A message's own timestamp: "14:28" today, then "Yesterday, 14:28", "9 Aug, 14:28". */
export function fullTime(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  return date.toDateString() === now.toDateString() ? formatTime(date) : formatRelative(date, { withTime: true, now });
}

const DAY_MS = 86_400_000;

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/**
 * The thread's day separators (A02-03): "Aujourd’hui", "Hier", then the day as
 * `formatWhen` writes it ("23 oct."); a day of another year carries its year
 * ("30 déc. 2025"), without the time `formatWhen` would add.
 */
export function dayLabel(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  const days = Math.round((startOfDay(now) - startOfDay(date)) / DAY_MS);
  if (days === 0) return i18n.t('thread.today');
  if (days === 1) return i18n.t('thread.yesterday');
  return date.getFullYear() === now.getFullYear() ? formatWhen(date, { now }) : formatDate(date);
}

/** How long a reply pauses automations, from Settings › Conversations (D-098): "15 min", "2 h", "1 jour". */
/**
 * Open, and the contact spoke last: someone owes them a reply. An automation
 * that answered counts as an answer (critique 2026-10-29, inbox P1 #3).
 */
export function isAwaitingReply(conversation: Pick<ConversationSummary, 'status' | 'lastInboundAt' | 'lastMessageAt'>): boolean {
  if (conversation.status !== 'open' || !conversation.lastInboundAt) return false;
  return !conversation.lastMessageAt || Date.parse(conversation.lastInboundAt) >= Date.parse(conversation.lastMessageAt);
}

/** How long since `iso`, in whole units: "12 min", "2 h", "3 jours". */
export function waitedFor(iso: string, now = Date.now()): string {
  return wholeDuration(now - Date.parse(iso));
}

function wholeDuration(ms: number): string {
  const minutes = Math.max(1, Math.floor(ms / 60_000));
  if (minutes < 60) return i18n.t('duration.minutes', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return i18n.t('duration.hours', { count: hours });
  return i18n.t('duration.days', { count: Math.floor(hours / 24) });
}

/**
 * Meta's standard messaging window, per channel: a reply is accepted for this
 * many hours after the contact's last message (the providers'
 * `messagingWindowHours`, D-169). A channel absent here has none.
 */
const REPLY_WINDOW_HOURS: Record<string, number> = { instagram: 24, messenger: 24 };
/** From here on, the reply box says how long is left. */
const CLOSING_SOON_MS = 3 * 3_600_000;

export type ReplyWindow = { state: 'open' } | { state: 'closing'; left: string } | { state: 'closed'; since: string | null };

/** Whether a reply can still reach the contact (critique 2026-10-29, inbox P1 #2). */
export function replyWindow(conversation: Pick<ConversationSummary, 'channel' | 'lastInboundAt'>, now = Date.now()): ReplyWindow {
  const hours = REPLY_WINDOW_HOURS[conversation.channel];
  if (hours === undefined) return { state: 'open' };
  if (!conversation.lastInboundAt) return { state: 'closed', since: null };
  const left = Date.parse(conversation.lastInboundAt) + hours * 3_600_000 - now;
  if (left <= 0) return { state: 'closed', since: waitedFor(conversation.lastInboundAt, now) };
  return left <= CLOSING_SOON_MS ? { state: 'closing', left: wholeDuration(left) } : { state: 'open' };
}

export function pauseLength(minutes: number): string {
  if (minutes < 60) return i18n.t('duration.minutes', { count: minutes });
  const hours = minutes / 60;
  if (hours < 24) return i18n.t('duration.hours', { count: hours });
  const days = hours / 24;
  return days === 7 ? i18n.t('duration.week') : i18n.t('duration.days', { count: days });
}

const BRAND_CHANNELS: Record<string, string> = {
  instagram: 'Instagram',
  messenger: 'Messenger',
};

export function channelLabel(code: string): string {
  if (code === 'mock') return i18n.t('inbox.mockChannel');
  return BRAND_CHANNELS[code] ?? code;
}

/** "@maya.haddad · Instagram"; the phone or email when the channel gives no handle. */
export function contactHandle(contact: ConversationContact, channel: string): string {
  const handle = contact.username
    ? contact.username.startsWith('@')
      ? contact.username
      : `@${contact.username}`
    : (contact.phone ?? contact.email);
  return [handle, channelLabel(channel)].filter(Boolean).join(' · ');
}
